// Small fetch helpers for /api. Errors come back as { message } from the Worker and Better Auth alike.
export class ApiError extends Error {
  readonly status: number;
  constructor(
    status: number,
    message: string,
  ) {
    super(message);
    this.status = status;
  }
}

/** A request with no answer in this long fails (#514), so a spinner never spins on a dead link. */
export const API_TIMEOUT_MS = 15_000;

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const stop = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    stop.abort();
  }, API_TIMEOUT_MS);
  const timeout = () => new ApiError(0, "Bloom isn't answering. Check your connection.");
  try {
    const res = await fetch(`/api${path}`, {
      method: init.method ?? (init.body === undefined ? "GET" : "POST"),
      headers: init.body === undefined ? undefined : { "content-type": "application/json" },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      credentials: "same-origin",
      signal: stop.signal,
    });
    const json = await res.json().catch(() => {
      if (timedOut) throw timeout();
      return null;
    });
    if (!res.ok) throw new ApiError(res.status, (json as { message?: string } | null)?.message ?? `Something went wrong (${res.status}).`);
    return json as T;
  } catch (e) {
    if (timedOut && !(e instanceof ApiError)) throw timeout();
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

export const auth = {
  signIn: (email: string, password: string) => api("/auth/sign-in/email", { body: { email, password, rememberMe: true } }),
  signOut: () => api("/auth/sign-out", { body: {} }),
  requestReset: (email: string) => api("/auth/request-password-reset", { body: { email, redirectTo: "/reset-password" } }),
  resetPassword: (token: string, newPassword: string) => api("/auth/reset-password", { body: { token, newPassword } }),
  resendVerification: (email: string) => api("/auth/send-verification-email", { body: { email, callbackURL: "/" } }),
};
